import React from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { DropDownList, MultiSelect } from '@progress/kendo-react-dropdowns';
import { Input, Switch } from '@progress/kendo-react-inputs';
import Modal from '../general/Modal';
import { getAuthToken, handleAuthTokenError, BASEURI, advancedFilter, handleAuthFailure } from '../utils';
import { RefreshPortal } from '../general/Layout';
import RefreshButton from '../general/RefreshButton';

const ALERT_FIELD_LABELS = {
    name: { id: 'app.alert.addEditAlertDialog.name', defaultMessage: 'Name' },
    graphs: { id: 'app.alert.addEditAlertDialog.graphs', defaultMessage: 'Graph(s)' },
    logic: { id: 'app.alert.addEditAlertDialog.logic', defaultMessage: 'Logic' },
    threshold: { id: 'app.alert.addEditAlertDialog.threshold', defaultMessage: 'Threshold' },
    message: { id: 'app.alert.addEditAlertDialog.message', defaultMessage: 'Message' },
    _type: { id: 'app.alert.addEditAlertDialog.alertType', defaultMessage: 'Alert Type' },
    category: { id: 'app.alert.addEditAlertDialog.autoAddGraphs', defaultMessage: 'Auto-Add Graphs' },
    email_alert: { id: 'app.alert.addEditAlertDialog.emailAlert', defaultMessage: 'Email Alert' },
    sms_alert: { id: 'app.alert.addEditAlertDialog.smsAlert', defaultMessage: 'SMS Alert' },
    whatsapp_alert: { id: 'app.alert.addEditAlertDialog.whatsAppAlert', defaultMessage: 'WhatsApp Alert' },
    call_alert: { id: 'app.alert.addEditAlertDialog.callAlert', defaultMessage: 'Call Alert' },
    min_time_beyond_threshold: { id: 'app.alert.addEditAlertDialog.hoursBeyondThreshold', defaultMessage: 'Hours Beyond Threshold' },
    snooze_time: { id: 'app.alert.addEditAlertDialog.suppressForHour', defaultMessage: 'Suppress For Hours' },
    call_cooldown_hours: { id: 'app.alert.addEditAlertDialog.callCooldownHours', defaultMessage: 'Call Cooldown Hours' },
    snooze: { id: 'app.alert.addEditAlertDialog.suppress', defaultMessage: 'Suppress' },
    on: { id: 'app.alert.addEditAlertDialog.enabled', defaultMessage: 'Enabled' },
};

function getFieldLabel(field, intl) {
    const labelConfig = ALERT_FIELD_LABELS[field];
    return labelConfig
        ? intl.formatMessage(labelConfig)
        : field.replace(/_/g, ' ');
}

function isValueEmpty(value) {
    if (value === undefined || value === null) {
        return true;
    }
    if (typeof value === 'string') {
        return value.trim() === '';
    }
    return false;
}

function isNumberEmpty(value) {
    return value === undefined || value === null || value === '';
}

function validateAlertBeforeSubmit(alert, intl) {
    const fieldErrors = {};

    const setRequired = (field) => {
        fieldErrors[field] = intl.formatMessage(
            { id: 'app.alert.addEditAlertDialog.fieldRequired', defaultMessage: '{field} is required.' },
            { field: getFieldLabel(field, intl) }
        );
    };

    const setMaxLength = (field, max) => {
        fieldErrors[field] = intl.formatMessage(
            { id: 'app.alert.addEditAlertDialog.fieldMaxLength', defaultMessage: '{field} must be at most {max} characters.' },
            { field: getFieldLabel(field, intl), max }
        );
    };

    const setInvalidNumber = (field) => {
        fieldErrors[field] = intl.formatMessage(
            { id: 'app.alert.addEditAlertDialog.validNumberRequired', defaultMessage: 'A valid number is required for {field}.' },
            { field: getFieldLabel(field, intl) }
        );
    };

    const name = typeof alert.name === 'string' ? alert.name.trim() : alert.name;
    if (isValueEmpty(name)) {
        setRequired('name');
    } else if (name.length > 100) {
        setMaxLength('name', 100);
    }

    const message = typeof alert.message === 'string' ? alert.message.trim() : alert.message;
    if (isValueEmpty(message)) {
        setRequired('message');
    } else if (message.length > 300) {
        setMaxLength('message', 300);
    }

    const logic = alert.logic?.value ?? alert.logic;
    if (isValueEmpty(logic)) {
        setRequired('logic');
    }

    const alertType = alert._type?.value ?? alert._type;
    if (isValueEmpty(alertType)) {
        setRequired('_type');
    }

    if (isNumberEmpty(alert.threshold)) {
        setRequired('threshold');
    } else if (Number.isNaN(Number(alert.threshold))) {
        setInvalidNumber('threshold');
    }

    if (isNumberEmpty(alert.min_time_beyond_threshold)) {
        setRequired('min_time_beyond_threshold');
    } else if (Number.isNaN(Number(alert.min_time_beyond_threshold))) {
        setInvalidNumber('min_time_beyond_threshold');
    }

    if (isNumberEmpty(alert.snooze_time)) {
        setRequired('snooze_time');
    } else if (Number.isNaN(Number(alert.snooze_time))) {
        setInvalidNumber('snooze_time');
    }

    if (!isNumberEmpty(alert.call_cooldown_hours) && Number.isNaN(Number(alert.call_cooldown_hours))) {
        setInvalidNumber('call_cooldown_hours');
    }

    return {
        isValid: Object.keys(fieldErrors).length === 0,
        fieldErrors,
    };
}

function formatApiErrorMessages(errors) {
    const list = Array.isArray(errors) ? errors : [errors];
    return list.map((err) => String(err)).join(' ');
}

function parseAlertApiErrors(errorBody) {
    const fieldErrors = {};
    let generalError = null;

    if (!errorBody || typeof errorBody !== 'object') {
        return { fieldErrors, generalError };
    }

    if (typeof errorBody.detail === 'string') {
        return { fieldErrors, generalError: errorBody.detail };
    }

    if (Array.isArray(errorBody.detail)) {
        return { fieldErrors, generalError: formatApiErrorMessages(errorBody.detail) };
    }

    for (const [field, errors] of Object.entries(errorBody)) {
        if (field === 'detail') {
            continue;
        }

        if (field === 'non_field_errors') {
            generalError = formatApiErrorMessages(errors);
            continue;
        }

        fieldErrors[field] = formatApiErrorMessages(errors);
    }

    return { fieldErrors, generalError };
}

const gridCtnStyles = {
    flexDirection: 'column',
    alignItems: 'center',
}

class SimpleGraphChip extends React.Component {

    render() {
        return (
            <span className="mdl-chip" style={{ backgroundColor: '#e5e5e5' }}>
                <span className="mdl-chip__text">{this.props.text}</span>
            </span>
        );
    }
}

class AddEditAlertDialog extends React.Component {
    constructor(props) {
        super(props)

        this.categories = [
            { text: this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.selectGraph', defaultMessage: 'Off (Select Graphs Manually)' }), value: null },
            { text: this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.temperature', defaultMessage: 'Temperature' }), value: 'temp' },
            { text: this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.paw', defaultMessage: 'PAW' }), value: 'paw' },
            { text: this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.relativeHumidity', defaultMessage: 'Relative Humidity' }), value: 'relative_humidity' },
            { text: this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.windSpeed', defaultMessage: 'Wind Speed' }), value: 'wind_speed' },
            { text: this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.dewPoint', defaultMessage: 'Dew Point' }), value: 'dew_point' },
        ]

    }

    itemRender(li, itemProps) {

        const itemChildren = <div> {li.props.children} [{itemProps.dataItem.calculation}] <div style={{ color: "#00F" }}> {itemProps.dataItem.chart.name} </div> </div>;
        return React.cloneElement(li, li.props, itemChildren);
    }

    fieldError(fieldName) {
        const { fieldErrors } = this.props;
        return fieldErrors && fieldErrors[fieldName];
    }

    render() {


        return this.props.show &&
            <Dialog title={this.props.addNew ?
                this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.addNew', defaultMessage: 'Add New' }) :
                this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.edit', defaultMessage: 'Edit' })} onClose={this.props.toggle} >
                <div>
                    {!this.props.allowSubmit &&
                        <span className='text-red-500'>
                            <sup>*</sup>
                            {this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.errorMessage', defaultMessage: 'Please provide email and phone Number in order to add alerts!' })}
                        </span>
                    }
                    {this.props.saveError &&
                        <div className='text-red-500 mb-3'>
                            {this.props.saveError}
                        </div>
                    }
                    <div className='modal-wrap'>
                        <div className='modal-form-wrap'>
                            <div className='alert-modal-form-inputs-wrap'>

                            <div className='modal-form-row'>
                                <Input
                                    required={true}
                                    name='name'
                                    label={this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.name', defaultMessage: 'NAME' })}
                                    value={this.props.alert.name}
                                    onChange={this.props.onChange}
                                    valid={!this.fieldError('name')}
                                    validationMessage={this.fieldError('name')}
                                />
                                <DropDownList
                                    label={this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.autoAddGraphs', defaultMessage: 'AUTO-ADD GRAPHS' })}
                                    name='category'
                                    data={this.categories}
                                    textField='text'
                                    dataItemKey='value'
                                    value={this.props.alert.category}
                                    onChange={this.props.onChange}
                                    valid={!this.fieldError('category')}
                                    validationMessage={this.fieldError('category')}
                                />

                            </div>
                            <div className='modal-form-row'>
                                <MultiSelect
                                    key='graph-select'
                                    name='graphs'
                                    // required={true}
                                    label={this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.graphs', defaultMessage: 'GRAPH(S)' })}
                                    data={this.props.graphsList}
                                    value={this.props.alert.graphs}
                                    textField='label'
                                    dataItemKey='id'
                                    onChange={this.props.onChange}
                                    itemRender={this.itemRender}
                                    filterable={true}
                                    onFilterChange={this.props.handleGraphFilterChange}
                                    style={{ height: 'auto' }}
                                    valid={!this.fieldError('graphs')}
                                    validationMessage={this.fieldError('graphs')}
                                />
                            </div>

                            <div className='modal-form-row'>
                                <DropDownList
                                    label={this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.logic', defaultMessage: 'LOGIC' })}
                                    name='logic'
                                    data={[{ text: '< Less than', value: 'lt' }, { text: '> Greater than', value: 'gt' }]}
                                    textField='text'
                                    dataItemKey='value'
                                    value={this.props.alert.logic}
                                    onChange={this.props.onChange}
                                    valid={!this.fieldError('logic')}
                                    validationMessage={this.fieldError('logic')}
                                />
                                <Input
                                    required={true}
                                    type={'number'}
                                    name='threshold'
                                    label={this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.threshold', defaultMessage: 'THRESHOLD' })}
                                    value={this.props.alert.threshold}
                                    onChange={this.props.onChange}
                                    valid={!this.fieldError('threshold')}
                                    validationMessage={this.fieldError('threshold')}
                                />
                            </div>

                            <div className='modal-form-row'>
                                <Input
                                    required={true}
                                    type={'number'}
                                    name='min_time_beyond_threshold'
                                    label={this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.hoursBeyondThreshold', defaultMessage: 'HOURS BEYOND THRESHOLD' })}
                                    value={this.props.alert.min_time_beyond_threshold}
                                    onChange={this.props.onChange}
                                    valid={!this.fieldError('min_time_beyond_threshold')}
                                    validationMessage={this.fieldError('min_time_beyond_threshold')}
                                />
                                <DropDownList
                                    label={this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.alertType', defaultMessage: 'ALERT TYPE' })}
                                    name='_type'
                                    data={[
                                        { value: 'i', text: 'ℹ Info' },
                                        { value: 'w', text: '⚠ Warning' },
                                        { value: 'c', text: '⚠ Critical' }]}
                                    textField='text'
                                    dataItemKey='value'
                                    value={this.props.alert._type}
                                    onChange={this.props.onChange}
                                    valid={!this.fieldError('_type')}
                                    validationMessage={this.fieldError('_type')}
                                />
                            </div>
                            <div className='modal-form-row'>
                                <div className="ag-switch-container mt-5 flex flex-wrap items-center gap-5 text-gray-500">
                                    <span>
                                        {this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.suppress', defaultMessage: 'SUPPRESS' })}
                                    </span>
                                    <Switch
                                        name='snooze'
                                        className="mr-[15px]"
                                        checked={this.props.alert.snooze}
                                        onChange={this.props.onChange}
                                    />
                                </div>
                                <Input
                                    required={true}
                                    type={'number'}
                                    name='snooze_time'
                                    label={this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.suppressForHour', defaultMessage: 'SUPRESS FOR HOURS' })}
                                    value={this.props.alert.snooze_time}
                                    onChange={this.props.onChange}
                                    valid={!this.fieldError('snooze_time')}
                                    validationMessage={this.fieldError('snooze_time')}
                                />
                            </div>
                            <div className='modal-form-row'>
                                <div className="ag-switch-container mt-5 flex flex-wrap items-center gap-5 text-gray-500">
                                    <span>
                                        {this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.emailAlert', defaultMessage: 'Email Alert' })}
                                    </span>
                                    <Switch
                                        name='email_alert'
                                        className="mr-[15px]"
                                        checked={this.props.alert.email_alert}
                                        onChange={this.props.onChange}
                                    />
                                </div>
                                {/* SMS Alert disabled (decision 2026-08-30)
                                <div className="ag-switch-container mt-5 flex flex-wrap items-center gap-5 text-gray-500">
                                    <span>
                                        {this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.smsAlert', defaultMessage: 'SMS Alert' })}
                                    </span>
                                    <Switch
                                        name='sms_alert'
                                        checked={this.props.alert.sms_alert}
                                        onChange={this.props.onChange}
                                    />
                                </div>
                                */}
                                <div className="ag-switch-container mt-5 flex flex-wrap items-center gap-5 text-gray-500">
                                    <span>
                                        {this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.whatsAppAlert', defaultMessage: 'WhatsApp Alert' })}
                                    </span>
                                    <Switch
                                        name='whatsapp_alert'
                                        className="mr-[15px]"
                                        checked={this.props.alert.whatsapp_alert}
                                        onChange={this.props.onChange}
                                    />
                                </div>
                            </div>
                            <div className='modal-form-row'>
                                <div className="ag-switch-container mt-5 flex flex-wrap items-center gap-5 text-gray-500">
                                    <span>
                                        {this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.callAlert', defaultMessage: 'Call Alert' })}
                                    </span>
                                    <Switch
                                        name='call_alert'
                                        className="mr-[15px]"
                                        checked={this.props.alert.call_alert}
                                        onChange={this.props.onChange}
                                    />
                                </div>
                                <Input
                                    type={'number'}
                                    name='call_cooldown_hours'
                                    label={this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.callCooldownHours', defaultMessage: 'CALL COOLDOWN HOURS' })}
                                    value={this.props.alert.call_cooldown_hours}
                                    onChange={this.props.onChange}
                                    valid={!this.fieldError('call_cooldown_hours')}
                                    validationMessage={this.fieldError('call_cooldown_hours')}
                                />
                            </div>
                            <div className='modal-form-row'>
                                <label className='k-form-field mt-5'>
                                    <span>{this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.message', defaultMessage: 'Message' })}</span>
                                    <textarea
                                        className={`bg-gray-100 rounded-md border-b-2 ${this.fieldError('message') ? 'border-red-500' : 'border-b-gray-300'}`}
                                        name='message'
                                        style={{ width: '100%', textAlign: 'start' }}
                                        required={true}
                                        value={this.props.alert.message || ''}
                                        onChange={this.props.onChange}
                                    />
                                    {this.fieldError('message') &&
                                        <span className='text-red-500 text-sm'>{this.fieldError('message')}</span>
                                    }
                                </label>
                            </div>
                            <div className='modal-form-row'>
                                <div className="ag-switch-container mt-5 flex flex-wrap items-center gap-5 text-gray-500">
                                    <span>
                                        {this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.enabled', defaultMessage: 'Enabled' })}
                                    </span>
                                    <Switch
                                        name='on'
                                        className="mr-[15px]"
                                        checked={this.props.alert.on}
                                        onChange={this.props.onChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                </div>
                <DialogActionsBar>
                    {this.props.addNew ? null : <button className="k-button k-button-md k-button-solid k-button-solid-base k-rounded-md delete" onClick={this.props.delete}>{this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.delete', defaultMessage: 'Delete' })}</button>}
                    <button className="k-button k-button-md k-button-solid k-button-solid-base k-rounded-md" onClick={this.props.toggle}>{this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.done', defaultMessage: 'DONE' })}</button>
                    <button className="k-button k-button-md k-button-solid k-button-solid-base k-rounded-md" onClick={this.props.save} disabled={!this.props.allowSubmit || this.props.saving}>
                        {this.props.saving
                            ? this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.saving', defaultMessage: 'Saving...' })
                            : this.props.addNew
                                ? this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.add', defaultMessage: 'Add' })
                                : this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.update', defaultMessage: 'Update' })}
                    </button>
                </DialogActionsBar>
            </Dialog>;
    }

}

class AlertConfig extends React.Component {

    render() {

        return (
            <div className='mdl-cell mdl-cell--8-col' >
                <form className='k-form' onSubmit={this.handleSubmit}>
                    <fieldset>
                        <legend>{this.props.intl.formatMessage({ id: 'app.alert.alertConfig.settings', defaultMessage: 'Settings' })}</legend>

                        <label className='k-form-field'>
                            <span>{this.props.intl.formatMessage({ id: 'app.alert.alertConfig.emailAddressComma', defaultMessage: 'Email addresses (comma separated)' })}</span>
                            <textarea
                                className='border-b-2 border-b-gray-300 bg-gray-100 rounded-md'
                                name='email_addresses'
                                style={{ width: '100%' }}
                                required={true}
                                onChange={this.props.onChange}
                                value={this.props.settings.email_addresses}
                            >

                            </textarea>
                            {this.props.emailValidMsg.length ? <p style={{ color: 'red' }}>{this.props.emailValidMsg}</p> : ""}
                        </label>

                        <div style={{ width: '100%' }}>
                            <Input
                                label={this.props.intl.formatMessage({ id: 'app.alert.alertConfig.phoneNo', defaultMessage: 'PHONE NUMBER' })}
                                name='phone_number'
                                required={true}
                                onChange={this.props.onChange}
                                value={this.props.settings.phone_number}
                                valid={this.props.phoneValid}
                                validationMessage={this.props.phoneValidMsg}
                                style={{ width: '100%' }}
                            />
                            {this.props.phoneValidMsg.length ? <p style={{ color: 'red' }}>{this.props.phoneValidMsg}</p> : ""}
                        </div>
                        <div style={{ width: '100%', marginTop: '1em' }}>
                            <Input
                                label={this.props.intl.formatMessage({ id: 'app.alert.alertConfig.callPhoneNumbers', defaultMessage: 'PHONE NUMBER(S) FOR CALLS' })}
                                name='call_phone_numbers'
                                onChange={this.props.onChange}
                                value={this.props.settings.call_phone_numbers}
                                valid={this.props.callPhoneValid}
                                validationMessage={this.props.callPhoneValidMsg}
                                style={{ width: '100%' }}
                            />
                            {this.props.callPhoneValidMsg.length ? <p style={{ color: 'red' }}>{this.props.callPhoneValidMsg}</p> : ""}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1em' }}>
                            <button
                                type='submit'
                                className='k-button k-button-md k-button-solid k-button-solid-base k-rounded-md k-primary'
                                style={{ height: 35 }}
                                value='Save'
                                required={true}
                                onClick={e => this.props.handleClick(e)}
                            >
                                {this.props.saving ?
                                    this.props.intl.formatMessage({ id: 'app.alert.alertConfig.saving', defaultMessage: 'Saving...' }) :
                                    this.props.intl.formatMessage({ id: 'app.alert.alertConfig.save', defaultMessage: 'Save' })}
                            </button>
                        </div>

                    </fieldset>
                </form>
            </div>
        );
    }

}

class AlertItem extends React.Component {

    render() {
        let graphs = this.props.alert.graphs.map(graph => <SimpleGraphChip key={graph.id} text={graph.label} />),
            type = {
                w: <span style={{ color: 'orange', fontSize: '1.5em' }} role="img"> ⚠ </span>,
                c: <span style={{ color: 'red', fontSize: '1.5em' }} role="img"> ⚠ </span>,
                i: <span style={{ color: 'skyblue', fontSize: '1.5em' }} role="img"> ℹ </span>
            },
            on = this.props.alert.on ? <span style={{ color: 'lightgreen' }} role="img">✓</span> : '❌';

        return (
            <tr name='alert_row' onClick={(e) => this.props.open(e, 'edit', this.props.id)} style={{ cursor: 'pointer' }}>
                <td className="mdl-data-table__cell--non-numeric">{this.props.alert.name}</td>
                <td className="mdl-data-table__cell--non-numeric">{type[this.props.alert._type]}</td>
                <td className="mdl-data-table__cell--non-numeric">{graphs}</td>
                <td className="mdl-data-table__cell--non-numeric">{on}</td>
            </tr>
        );
    }

}

class AlertTable extends React.Component {

    render() {
        return (
            <div className='mdl-cell mdl-cell--8-col'>
                <form className='k-form'>
                    <fieldset>
                        <legend>{this.props.intl.formatMessage({ id: 'app.alert.alertTable.alerts', defaultMessage: 'Alerts' })}</legend>
                        <table className="mdl-data-table mdl-js-data-table alerts-table" style={{ width: '100%', marginTop: '10px' }}>
                            <thead>
                                <tr >
                                    <th className="mdl-data-table__cell--non-numeric" style={{ textAlign: "left" }}>{this.props.intl.formatMessage({ id: 'app.alert.alertTable.alert', defaultMessage: 'Alert' })}</th>
                                    <th style={{ textAlign: "left" }}>{this.props.intl.formatMessage({ id: 'app.alert.alertTable.type', defaultMessage: 'Type' })}</th>
                                    <th style={{ textAlign: "left" }}>{this.props.intl.formatMessage({ id: 'app.alert.alertTable.graphs', defaultMessage: 'Graphs' })}</th>
                                    <th style={{ textAlign: "left" }}>{this.props.intl.formatMessage({ id: 'app.alert.alertTable.enabled', defaultMessage: 'Enabled' })}</th>
                                </tr>
                            </thead>
                            <tbody>

                                {this.props.children}

                            </tbody>
                        </table>
                    </fieldset>
                </form>
            </div>
        );
    }
}

class Alerts extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            alerts: [],
            settings: {
                email_addresses: '',
                phone_number: '',
                call_phone_numbers: ''
            },
            hasSettings: false,
            savingSettings: false,
            showDialog: false,
            addNew: false,
            newAlert: {

            },
            graphsList: [],
            notInitial: false,
            isWorking: {
                loadSettings: false,
                loadAlerts: false,
                loadGraphsList: false
            },
            phoneIsValid: true,
            emailIsValid: true,
            callPhoneIsValid: true,
            savingAlert: false,
            alertSaveError: null,
            alertFieldErrors: {},
            phoneValidMsg: "",
            emailValidMsg: "",
            callPhoneValidMsg: "",
            loading: false,
        }

        this.loadSettings = this.loadSettings.bind(this);
        this.loadAlerts = this.loadAlerts.bind(this);
        this.handleSettingsChange = this.handleSettingsChange.bind(this);
        this.handleAlertTableChange = this.handleAlertTableChange.bind(this);
        this.handleSettingsSave = this.handleSettingsSave.bind(this);
        this.toggleDialog = this.toggleDialog.bind(this);
        this.handleAlertDialogChange = this.handleAlertDialogChange.bind(this);
        this.handleSettingsSave = this.handleSettingsSave.bind(this);
        this.loadGraphsList = this.loadGraphsList.bind(this);
        this.handleAlertSave = this.handleAlertSave.bind(this);
        this.normalizeAlert = this.normalizeAlert.bind(this);
        this.handleAlertDelete = this.handleAlertDelete.bind(this);
        this.handleGraphFilterChange = this.handleGraphFilterChange.bind(this);
    }

    handleGraphFilterChange(event) {
        advancedFilter(
            event.filter.value,
            this.graphsListSource,
            (graphsList) => this.setState({ graphsList }),
            ['label', 'name', 'calculation'])
    }

    loadSettings() {
        this.setState({ loading: true });

        let url = `${BASEURI}/api/monitor/conf/listcreate/`,
            options = {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                }
            };

        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {
                if (token === false) {
                    this.setState({ loading: false });
                    return false;
                }

                options.headers.Authorization = `Bearer  ${token}`;

                fetch(url, options)
                    .then((response) => {
                        if (response.status === 401) {
                            handleAuthFailure();
                            return [];
                        }
                        if (!response.ok) {
                            throw new Error(`Failed to load alert settings (${response.status})`);
                        }
                        return response.json();
                    })
                    .then((settings) => {
                        const normalizedSettings = Array.isArray(settings) ? settings : [];
                        if (!this.state.notInitial) {
                            this.setState({ notInitial: true });
                        }
                        if (normalizedSettings.length > 0)
                            this.setState({
                                settings: normalizedSettings[0],
                                hasSettings: true,
                                loading: false,
                            });
                        else {
                            this.setState({ loading: false });
                        }
                    })
                    .catch(error => {
                        console.error('Error:', error);
                        this.setState({ loading: false });
                    });

            });

    }

    loadAlerts() {
        this.setState({ loading: true });

        let url = `${BASEURI}/api/monitor/alerts/list/`,
            options = {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                }
            };

        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {
                if (token === false) {
                    this.setState({ loading: false, alerts: [] });
                    return false;
                }

                options.headers.Authorization = `Bearer  ${token}`;

                fetch(url, options)
                    .then((response) => {
                        if (response.status === 401) {
                            handleAuthFailure();
                            return [];
                        }
                        if (!response.ok) {
                            throw new Error(`Failed to load alerts (${response.status})`);
                        }
                        return response.json();
                    })
                    .then((alerts) => {
                        this.setState({
                            loading: false,
                            alerts: Array.isArray(alerts) ? alerts : [],
                        });
                    })
                    .catch(error => {
                        console.error('Error:', error);
                        this.setState({ loading: false, alerts: [] });
                    });
            });

    }

    loadGraphsList() {
        this.setState({ loading: true });

        let url = `${BASEURI}/api/allgraphs/`,
            options = {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                }
            };

        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {
                if (token === false) {
                    this.setState({ loading: false, graphsList: [] });
                    return false;
                }

                options.headers.Authorization = `Bearer  ${token}`;

                fetch(url, options)
                    .then((response) => {
                        if (response.status === 401) {
                            handleAuthFailure();
                            return [];
                        }
                        if (!response.ok) {
                            throw new Error(`Failed to load graphs list (${response.status})`);
                        }
                        return response.json();
                    })
                    .then((graphsList) => {
                        const normalizedGraphsList = Array.isArray(graphsList) ? graphsList : [];
                        if (normalizedGraphsList.length > 0)
                            this.setState({
                                graphsList: normalizedGraphsList,
                                loading: false
                            });
                        else {
                            this.setState({ loading: false, graphsList: [] });
                        }
                        this.graphsListSource = normalizedGraphsList;
                    })
                    .catch(error => {
                        console.error('Error:', error);
                        this.setState({ loading: false, graphsList: [] });
                    });

            });

    }

    componentDidMount() {
        this.loadSettings();
        this.loadAlerts();
        this.loadGraphsList();

        // document.getElementById('refresh').addEventListener('click', this.refresh);
    }

    componentWillUnmount() {
        // document.getElementById('refresh').removeEventListener('click', this.refresh);
    }

    refresh = () => {
        console.log('reloading alerts');
        this.loadSettings();
        this.loadAlerts();
        this.loadGraphsList();
    }



    handleSettingsChange(event) {

        const target = event.target;

        let settings = this.state.settings;
        settings[target.name] = target.value;

        const phoneNumberValidator = /^(\+\d{11,12}|00\d{11,12})?(\s)*(,?(\s)*\+\d{11,12}|,00\d{11,12})*$/;
        const emailValidator = /^([a-zA-Z0-9&_\\.-]{1,}@[a-z&_\\.-]{1,}\.[a-zA-Z0-9&_\\.-]{1,})?(,?(\s)*[a-zA-Z0-9&_\\.-\s]{1,}@[a-zA-Z0-9&_\\.-\s]{1,}\.[a-z&_\\.-\s]{1,})*$/g;
        if (target.name === 'phone_number') {
            let phoneValidResult = phoneNumberValidator.test(target.value);
            phoneValidResult ? this.setState({ phoneIsValid: true, phoneValidMsg: "" }) : this.setState({
                phoneIsValid: false,
                phoneValidMsg: this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.phoneNumber', defaultMessage: 'PHONE NUMBER(S) IS/ARE NOT VALID!' })
            });
        }
        else if (target.name === 'call_phone_numbers') {
            let callPhoneValidResult = target.value === '' || phoneNumberValidator.test(target.value);
            callPhoneValidResult ? this.setState({ callPhoneIsValid: true, callPhoneValidMsg: "" }) : this.setState({
                callPhoneIsValid: false,
                callPhoneValidMsg: this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.phoneNumber', defaultMessage: 'PHONE NUMBER(S) IS/ARE NOT VALID!' })
            });
        }
        else {
            let emailValidResult = emailValidator.test(target.value);
            emailValidResult ? this.setState({ emailIsValid: true }) : this.setState({ emailIsValid: false });
        }

        this.setState({ settings: settings });

    }

    componentDidUpdate() {
        // console.log(this.state.newAlert);    
    }

    handleSettingsSave(event) {

        this.setState({ savingSettings: true });

        event.preventDefault();
        if (this.state.settings.phone_number && !this.state.phoneIsValid) {
            this.setState({
                savingSettings: false, emailValidMsg: "",
                phoneValidMsg: this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.phoneNumber', defaultMessage: 'PHONE NUMBER(S) IS/ARE NOT VALID!' })
            });
            return
        }
        if (this.state.settings.email_addresses && !this.state.emailIsValid) {
            this.setState({
                savingSettings: false, phoneValidMsg: "",
                emailValidMsg: this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.email', defaultMessage: 'EMAIL ADDRESS(ES) IS/ARE NOT VALID!' })
            });
            return
        }

        if (this.state.settings.call_phone_numbers && !this.state.callPhoneIsValid) {
            this.setState({
                savingSettings: false,
                callPhoneValidMsg: this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.phoneNumber', defaultMessage: 'PHONE NUMBER(S) IS/ARE NOT VALID!' })
            });
            return
        }

        this.setState({ phoneValidMsg: "", emailValidMsg: "", callPhoneValidMsg: "" });
        let url = this.state.hasSettings
            ? `${BASEURI}/api/monitor/conf/rud/${this.state.settings.user}/`
            : `${BASEURI}/api/monitor/conf/listcreate/`,
            options = {
                method: this.state.hasSettings ? 'PUT' : 'POST',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                }
            };

        options.body = JSON.stringify(this.state.settings);

        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {
                if (token === false) {
                    this.setState({ savingSettings: false });
                    return false;
                }

                options.headers.Authorization = `Bearer  ${token}`;

                fetch(url, options)
                    .then((response) => response.json())
                    .then((settings) => {

                        this.setState({
                            settings: settings,
                            hasSettings: settings ? true : false,
                            savingSettings: false
                        });

                    })
                    .catch(error => console.log('Error:', error));

            });

    }

    handleAlertTableChange() {

    }

    toggleDialog(event, op, alertId = null) {

        if (op === 'edit') {
            // let alertId = event.target.id,
            let alertToEdit, alerts = this.state.alerts;
            //find the alert that is supposed to be edited
            for (let alert in alerts) {
                if (alerts[alert].id === alertId)
                    alertToEdit = alerts[alert];
            }

            this.setState(state => ({
                showDialog: !state.showDialog,
                //if alert is found set newAlert to it otherwise keep old value
                newAlert: alertToEdit !== undefined ? this.normalizeAlert(alertToEdit) : state.newAlert,
                addNew: false,
                alertSaveError: null,
                alertFieldErrors: {},
            }));

        } else {

            this.setState(state => ({
                showDialog: !state.showDialog,
                addNew: true,
                alertSaveError: null,
                alertFieldErrors: {},
                newAlert: {
                    'on': true,
                    'snooze': false,
                    'email_alert': false,
                    'sms_alert': false,
                    'whatsapp_alert': false,
                    'call_alert': false,
                    'snooze_time': 4,
                    'call_cooldown_hours': 12
                },
            }));
        }
    }

    handleAlertDialogChange(event) {

        let newAlert = this.state.newAlert;

        //the name attribute in KendoReact Switch element
        //must be accessed using props
        if (event.target.name !== undefined)
            newAlert[event.target.name] = event.target.value;
        else
            newAlert[event.target.props.name] = event.target.value;

        this.setState({ newAlert: newAlert, alertSaveError: null, alertFieldErrors: {} });

    }

    handleAlertSave(event) {

        const validation = validateAlertBeforeSubmit(this.state.newAlert, this.props.intl);
        if (!validation.isValid) {
            this.setState({
                alertFieldErrors: validation.fieldErrors,
                alertSaveError: null,
            });
            return;
        }

        this.setState({ savingAlert: true, alertSaveError: null, alertFieldErrors: {} });

        const { newAlert, addNew } = this.state;
        const payload = {
            ...newAlert,
            graphs: (newAlert.graphs || []).map((graph) =>
                graph && typeof graph === 'object' ? graph.id : graph
            ),
            logic: newAlert.logic?.value ?? newAlert.logic,
            _type: newAlert._type?.value ?? newAlert._type,
            category: newAlert.category ? newAlert.category.value : null,
        };

        let url = addNew
            ? `${BASEURI}/api/monitor/alerts/create/`
            : `${BASEURI}/api/monitor/alerts/rud/${newAlert.id}/`,
            options = {
                method: addNew ? 'POST' : 'PUT',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                }
            };

        options.body = JSON.stringify(payload);

        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {
                if (token === false) {
                    this.setState({ savingAlert: false });
                    return false;
                }

                options.headers.Authorization = `Bearer  ${token}`;

                fetch(url, options)
                    .then((response) => {
                        if (!response.ok) {
                            return response.json()
                                .catch(() => ({}))
                                .then((errorBody) => {
                                    const parsed = parseAlertApiErrors(errorBody);
                                    const validationError = new Error(parsed.generalError || 'Validation failed');
                                    validationError.fieldErrors = parsed.fieldErrors;
                                    validationError.generalError = parsed.generalError;
                                    throw validationError;
                                });
                        }
                        return response.json();
                    })
                    .then((savedAlert) => {

                        let normalizedAlert = this.normalizeAlert(savedAlert);
                        savedAlert.graphs = normalizedAlert.graphs;

                        this.setState(state => {

                            let alerts = state.alerts
                            let changedAlert;
                            for (let i in alerts) {
                                if (savedAlert.id === alerts[i].id) {
                                    changedAlert = i;
                                    break;
                                }
                            }

                            if (changedAlert !== undefined)
                                alerts.splice(changedAlert, 1, savedAlert);
                            else
                                alerts.push(savedAlert);

                            return {
                                alerts: alerts,
                                newAlert: this.normalizeAlert(savedAlert),
                                savingAlert: false,
                                showDialog: false,
                                alertSaveError: null,
                                alertFieldErrors: {},
                            };

                        });

                    })
                    .catch(error => {
                        console.error('Error:', error);
                        this.setState({
                            savingAlert: false,
                            alertFieldErrors: error.fieldErrors || {},
                            alertSaveError: error.fieldErrors
                                ? (error.generalError || null)
                                : (error.message || this.props.intl.formatMessage({
                                    id: 'app.alert.addEditAlertDialog.saveError',
                                    defaultMessage: 'Failed to save alert.',
                                })),
                        });
                    });

            });

    }

    normalizeAlert(alertToNormalize) {

        let alert = {};
        alert = Object.assign(alert, alertToNormalize);

        let logicValues = {
            'lt': { text: '< Less than', value: 'lt' },
            'gt': { text: '> Greater than', value: 'gt' }
        }

        let typeValues = {
            'i': { value: 'i', text: 'ℹ Info' },
            'w': { value: 'w', text: '⚠ Warning' },
            'c': { value: 'c', text: '⚠ Critical' }
        }
        let categoryValues = {
            null: { text: this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.selectGraph', defaultMessage: 'Off (Select Graphs Manually)' }), value: null },
            'relative_humidity': { text: this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.relativeHumidity', defaultMessage: 'Relative Humidity' }), value: 'relative_humidity' },
            'temp': { text: this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.temperature', defaultMessage: 'Temperature' }), value: 'temp' },
            'paw': { text: this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.paw', defaultMessage: 'PAW' }), value: 'paw' },
            'wind_speed': { text: this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.windSpeed', defaultMessage: 'Wind Speed' }), value: 'wind_speed' },
            'dew_point': { text: this.props.intl.formatMessage({ id: 'app.alert.addEditAlertDialog.dewPoint', defaultMessage: 'Dew Point' }), value: 'dew_point' }

        }

        //only normalize graphs if not already nomalized
        if (!Array.isArray(alert.graphs)) {
            alert.graphs = [];
        }

        if (alert.graphs.length > 0) {
            if (!alert.graphs[0].hasOwnProperty('label')) {
                let graphs = [],
                    graphsList = this.state.graphsList.slice();
                for (let i in graphsList) {
                    if (alert.graphs.indexOf(graphsList[i].id) !== -1) {
                        graphs.push(graphsList[i]);
                    }

                }
                alert.graphs = graphs;
            }
        }


        alert.logic = logicValues[alert.logic];
        alert._type = typeValues[alert._type];
        alert.category = categoryValues[alert.category]

        return alert;

    }

    handleAlertDelete() {

        if (!window.confirm(this.props.intl.formatMessage({ id: 'app.alert.confrimMessage', defaultMessage: 'Are you sure want to delete alert: [{name}]?' }, { name: this.state.newAlert.name })))
            return

        this.setState({ loading: true });

        let alertToDelete = this.state.newAlert.id;

        let url = `${BASEURI}/api/monitor/alerts/rud/${alertToDelete}/`,
            options = {
                method: 'DELETE',
                headers: {
                    "Content-Type": "application/json; charset=utf-8"
                }
            }

        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {
                if (token === false) {
                    this.setState({ loading: false });
                    return false;
                }

                options.headers.Authorization = `Bearer  ${token}`;

                fetch(url, options)
                    .then(response => {
                        let alertList = this.state.alerts.slice();
                        let alertToDeleteIndex = null;
                        for (let i = 0; i < alertList.length; i++) {
                            if (alertList[i].id === alertToDelete) {
                                alertToDeleteIndex = i;
                                break;
                            }
                        }

                        alertList.splice(alertToDeleteIndex, 1);

                        this.setState({ alerts: alertList, showDialog: false });
                    });

            });

    }

    render() {
        const alertList = Array.isArray(this.state.alerts) ? this.state.alerts : [];
        let alerts = alertList.map(alert => <AlertItem open={this.toggleDialog} key={alert.id} id={alert.id} alert={alert} />);

        return (
            <div className='mdl-grid' style={gridCtnStyles}>
                <RefreshPortal>
                    <RefreshButton loading={this.state.loading} onClick={this.refresh} />
                </RefreshPortal>

                <div className='breadcrumb mdl-grid mdl-cell mdl-cell--12-col'>
                    {this.props.intl.formatMessage({ id: 'app.alert.alerts', defaultMessage: 'ALERTS' })}
                </div>
                <AlertConfig
                    intl={this.props.intl}
                    settings={this.state.settings}
                    onChange={this.handleSettingsChange}
                    handleClick={this.handleSettingsSave}
                    phoneValid={this.state.phoneIsValid}
                    emailValid={this.state.emailIsValid}
                    callPhoneValid={this.state.callPhoneIsValid}
                    saving={this.state.savingSettings}
                    phoneValidMsg={this.state.phoneValidMsg}
                    emailValidMsg={this.state.emailValidMsg}
                    callPhoneValidMsg={this.state.callPhoneValidMsg}
                />

                <AlertTable intl={this.props.intl}>
                    {alerts}
                </AlertTable>

                <Modal>
                    <AddEditAlertDialog
                        intl={this.props.intl}
                        onChange={this.handleAlertDialogChange}
                        alert={this.state.newAlert}
                        toggle={this.toggleDialog}
                        show={this.state.showDialog}
                        save={this.handleAlertSave}
                        saving={this.state.savingAlert}
                        saveError={this.state.alertSaveError}
                        fieldErrors={this.state.alertFieldErrors}
                        addNew={this.state.addNew}
                        graphsList={this.state.graphsList}
                        delete={this.handleAlertDelete}
                        handleGraphFilterChange={this.handleGraphFilterChange}
                        allowSubmit={Boolean(this.state.settings.email_addresses) & Boolean(this.state.settings.phone_number)}
                    />
                </Modal>

                <button name='add_new_button' onClick={(e) => this.toggleDialog(e, 'new')} className="mdl-button mdl-js-button mdl-button--fab mdl-js-ripple-effect mdl-button--colored mdl-shadow--4dp mdl-color--primary ag-add-button">
                    <i className="material-icons" role="presentation">add_alerts</i>
                    <span className="visuallyhidden">{this.props.intl.formatMessage({ id: 'app.alert.Add', defaultMessage: 'Add' })}</span>
                </button>

            </div>
        )
    }

}

export default Alerts;
